import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clampToViewport,
  exceedsDragThreshold,
  reanchorToNearestEdges,
  type Size,
  type Viewport,
} from './drag-math';
import type { Position } from './position-storage';

/** Movement (in px) before a pointerdown→pointerup becomes a drag. */
const DRAG_THRESHOLD_PX = 5;

export interface UseDraggableOptions {
  initialPosition: Position;
  /**
   * Fired when a position is committed (drag release, or a re-anchor after
   * the viewport/element changed size). `geometry` is what the position was
   * measured against — a persisting caller must store it, since the
   * coordinates are meaningless on a different-sized screen.
   */
  onPositionChange?: (pos: Position, geometry: { viewport: Viewport; size: Size }) => void;
  onClick?: () => void;
}

export interface UseDraggableResult {
  isDragging: boolean;
  bindToElement: (el: HTMLElement | null) => void;
}

/**
 * Drag a position-fixed element with GPU-accelerated `transform` mutations.
 *
 * Performance note: during pointermove we mutate `el.style.transform` directly
 * (no React state update, no re-render). Only on pointerup do we commit the
 * final position to React state — that's when persistence callbacks fire and
 * the position becomes part of the rendered tree. This keeps drag at native
 * frame rate even with a large React tree above.
 *
 * The caller's element must be `position: fixed; top: 0; left: 0`. We move it
 * via `transform: translate3d(x, y, 0)`.
 */
export function useDraggable(opts: UseDraggableOptions): UseDraggableResult {
  const { initialPosition, onPositionChange, onClick } = opts;

  const [position, setPosition] = useState<Position>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  // The bound element lives in state (not a plain ref) so the listener
  // effect can re-run when the host first attaches the DOM node. A pure
  // ref wouldn't notify React that the element became available.
  const [el, setEl] = useState<HTMLElement | null>(null);
  const positionRef = useRef<Position>(initialPosition);
  // The {viewport, size} the current position is anchored against — i.e.
  // what it was last reconciled with. Seeded when the element binds, then
  // kept current every time we re-anchor (window resize or element resize).
  const lastGeometryRef = useRef<{ viewport: Viewport; size: Size } | null>(null);

  // Stash callbacks in refs so the pointer-event effect can read the
  // latest value without re-running. Without this, a fresh `onClick`
  // reference from the parent (recreated each render) would tear down
  // and re-attach the listeners mid-drag.
  const onClickRef = useRef(onClick);
  const onPositionChangeRef = useRef(onPositionChange);
  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);
  useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
  }, [onPositionChange]);

  // Apply position to DOM whenever React state changes (initial mount,
  // post-drag commit, re-anchor). During drag we mutate the
  // transform directly bypassing React, so this effect doesn't fight us.
  const posX = position.x;
  const posY = position.y;
  useEffect(() => {
    positionRef.current = { x: posX, y: posY };
    if (el) {
      el.style.transform = makeTransform(posX, posY);
    }
  }, [posX, posY, el]);

  // Re-anchor on viewport or element resize: a stored position means
  // "anchored to an edge", not "at this pixel", so a plain clamp is
  // one-directional — it rescues a button that fell outside a shrinking
  // viewport, but leaves it stranded mid-screen when the viewport grows
  // back out. reanchorToNearestEdges preserves the gap to whichever edge
  // is nearer, on both axes, so the anchor survives shrink-then-grow.
  // We also re-persist so the reconciled position survives reload.
  useEffect(() => {
    if (!el) return;

    function reanchor() {
      const nextGeometry = measureGeometry(el!);
      const fromGeometry = lastGeometryRef.current ?? nextGeometry;
      const reanchored = reanchorToNearestEdges(positionRef.current, fromGeometry, nextGeometry);
      lastGeometryRef.current = nextGeometry;
      if (reanchored.x !== positionRef.current.x || reanchored.y !== positionRef.current.y) {
        setPosition(reanchored);
        onPositionChangeRef.current?.(reanchored, nextGeometry);
      }
    }

    window.addEventListener('resize', reanchor);

    // Also observe the element itself: its width changes where the round
    // button gives way to the ~220px advisor (DESKTOP_FAB_QUERY), and can
    // change independently of the window (label font loading, locale
    // switch). A window-resize listener alone misses element-only changes.
    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => reanchor());
      resizeObserver.observe(el);
    }

    // Run once on mount in case the viewport or element size changed
    // between the useMemo'd initial computation and the actual paint.
    reanchor();

    return () => {
      window.removeEventListener('resize', reanchor);
      resizeObserver?.disconnect();
    };
  }, [el]);

  const bindToElement = useCallback((node: HTMLElement | null) => {
    if (node) {
      // Apply initial transform immediately so there's no flash at mount.
      node.style.transform = makeTransform(positionRef.current.x, positionRef.current.y);
      // Seed the anchor geometry so the very first reanchor() call (above)
      // has a `from` to compare against.
      lastGeometryRef.current = measureGeometry(node);
    }
    setEl(node);
  }, []);

  useEffect(() => {
    if (!el) return;

    let pointerStart: Position | null = null;
    let elementStart: Position | null = null;
    let didMove = false;
    let activePointerId: number | null = null;

    function onPointerDown(e: PointerEvent) {
      if (e.button !== 0) return;
      pointerStart = { x: e.clientX, y: e.clientY };
      elementStart = { ...positionRef.current };
      didMove = false;
      activePointerId = e.pointerId;
      el!.setPointerCapture(e.pointerId);
    }

    function onPointerMove(e: PointerEvent) {
      if (pointerStart === null || elementStart === null || e.pointerId !== activePointerId)
        return;
      const current = { x: e.clientX, y: e.clientY };
      if (!didMove && exceedsDragThreshold(pointerStart, current, DRAG_THRESHOLD_PX)) {
        didMove = true;
        setIsDragging(true);
      }
      if (didMove) {
        const { viewport, size } = measureGeometry(el!);
        const next = clampToViewport(
          {
            x: elementStart.x + (current.x - pointerStart.x),
            y: elementStart.y + (current.y - pointerStart.y),
          },
          viewport,
          size,
        );
        // Direct DOM mutation — bypasses React, runs at native frame rate.
        el!.style.transform = makeTransform(next.x, next.y);
        positionRef.current = next;
      }
    }

    function onPointerUp(e: PointerEvent) {
      if (e.pointerId !== activePointerId) return;
      try {
        el!.releasePointerCapture(e.pointerId);
      } catch {
        // pointerId already released — ignore
      }
      activePointerId = null;
      const wasDrag = didMove;
      pointerStart = null;
      elementStart = null;
      didMove = false;
      setIsDragging(false);
      if (wasDrag) {
        // Snap horizontally to whichever viewport edge the button's
        // center is closer to (Messenger chat-head behaviour).
        const geometry = measureGeometry(el!);
        const { viewport, size } = geometry;
        const centerX = positionRef.current.x + size.width / 2;
        const snappedX =
          centerX < viewport.width / 2 ? 0 : Math.max(0, viewport.width - size.width);
        const finalPos = { x: snappedX, y: positionRef.current.y };

        // Animate the snap with a CSS transition. We add the transition
        // inline, kick the new transform, then clear the transition once
        // the animation finishes so subsequent drags are instant.
        el!.style.transition = 'transform 250ms cubic-bezier(0.2, 0.8, 0.2, 1)';
        el!.style.transform = makeTransform(finalPos.x, finalPos.y);
        positionRef.current = finalPos;
        setTimeout(() => {
          el!.style.transition = '';
        }, 300);

        // Commit final position so React state matches the DOM and the
        // onPositionChange persistence callback fires.
        setPosition(finalPos);
        onPositionChangeRef.current?.(finalPos, geometry);
        swallowNextClick();
      } else {
        onClickRef.current?.();
      }
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, [el]);

  return { isDragging, bindToElement };
}

function makeTransform(x: number, y: number): string {
  // translate3d forces GPU compositing layer on most browsers
  return `translate3d(${x}px, ${y}px, 0)`;
}

/**
 * The area a fixed element is actually visible in. innerWidth counts a classic (Windows) scrollbar, which
 * then hides the right edge of a button docked there, and grows on phones when the host page overflows
 * sideways; the root's client box has neither. min(): in quirks mode the root's clientHeight is the whole
 * document, and jsdom reports 0.
 */
export function viewportSize(): Viewport {
  const root = document.documentElement;
  return {
    width: Math.min(root.clientWidth || Infinity, window.innerWidth),
    height: Math.min(root.clientHeight || Infinity, window.innerHeight),
  };
}

/**
 * A mouse drag still ends in a native click on the button. Stop it at the window,
 * before page-level click tracking (GTM) counts a moved button as an opened chat.
 * The click follows pointerup in the same task, so the guard is gone by the next one.
 */
function swallowNextClick(): void {
  const stop = (e: Event) => e.stopPropagation();
  window.addEventListener('click', stop, { capture: true, once: true });
  setTimeout(() => window.removeEventListener('click', stop, { capture: true }), 0);
}

function measureGeometry(el: HTMLElement): { viewport: Viewport; size: Size } {
  const rect = el.getBoundingClientRect();
  return {
    viewport: viewportSize(),
    size: { width: rect.width, height: rect.height },
  };
}
